import FeatureGates, { GetExperimentValueOptions } from "@atlaskit/feature-gate-js-client";

type ExperimentClientParams<T> = {
  experimentName: string;
  parameterName: string;
  defaultValue: T;
  options?: GetExperimentValueOptions<T>;
};
type AssurePrimitives<T> = T extends string ? string : T extends number ? number : T extends boolean ? boolean : T;

/**
 * Returns the value of a given parameter in an experiment config, automatically firing exposure.
 *
 * @param {string} experimentName - The name of the experiment
 * @param {string} parameterName - The name of the parameter to fetch from the experiment config
 * @param {T} defaultValue - The value to serve if the experiment or parameter do not exist, or if the returned value does not match the expected type.
 */
export const getExperiment = <T>({
  experimentName,
  parameterName,
  defaultValue,
  options,
}: ExperimentClientParams<T>): AssurePrimitives<T> => {
  return FeatureGates.getExperimentValue(experimentName, parameterName, defaultValue, options) as AssurePrimitives<T>;
};

/**
 * Returns the value of a given parameter in an experiment config, without firing exposure. Only use this if you plan on firing exposure manually.
 * This method should be used only when absolutely necessary. More info: https://www.statsig.com/blog/when-allocation-point-and-exposure-point-differ
 *
 * @param {string} experimentName - The name of the experiment
 * @param {string} parameterName - The name of the parameter to fetch from the experiment config
 * @param {T} defaultValue - The value to serve if the experiment or parameter do not exist, or if the returned value does not match the expected type.
 */
export const getExperimentNoExposure = <T>({
  experimentName,
  parameterName,
  defaultValue,
}: ExperimentClientParams<T>): AssurePrimitives<T> => {
  return FeatureGates.getExperimentValue(experimentName, parameterName, defaultValue, {
    fireExperimentExposure: false,
  }) as AssurePrimitives<T>;
};

/**
 * Manually logs exposure for an experiment. Only use this if you used `getExperimentNoExposure`.
 * More info: https://www.statsig.com/blog/when-allocation-point-and-exposure-point-differ
 *
 * @param {string} experimentName - The name of the experiment
 */
export const manuallyLogExposure = (experimentName: string): void => {
  FeatureGates.manuallyLogExperimentExposure(experimentName);
};
